import { useEffect, useState } from "react";

import {
  fetchNotifications,
  clearNotifications,
  deleteNotification,
  markNotificationRead,
  type NotificationItem,
} from "./chat";
import { subscribeRealtime } from "./realtime";

export function useNotifications(userId?: string) {
  const [data, setData] = useState<NotificationItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setData([]);
      setLoading(false);
      setError(null);

      return;
    }

    let isMounted = true;

    setLoading(true);

    fetchNotifications(userId)
      .then((notifications) => {
        if (!isMounted) return;
        setData(notifications);
        setError(null);
      })
      .catch(() => {
        if (!isMounted) return;
        setError("Impossible de charger les notifications.");
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    const unsubscribe = subscribeRealtime((event) => {
      if (event.type !== "notification:created") return;
      const incoming = event.data.notification;

      if (String(incoming.userId) !== String(userId)) return;
      setData((prev) => {
        const next = prev ? prev.slice() : [];

        if (next.some((item) => item._id === incoming._id)) return prev;

        return [incoming, ...next];
      });
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [userId]);

  const markRead = (id: string) =>
    markNotificationRead(id).then((updated) => {
      setData((prev) =>
        prev
          ? prev.map((item) => (item._id === id ? updated : item))
          : [updated],
      );
    });

  const clearAll = () => {
    if (!userId) {
      setData([]);

      return Promise.resolve();
    }

    return clearNotifications(userId).then(() => {
      setData([]);
    });
  };

  const removeNotification = (id: string) =>
    deleteNotification(id).then(() => {
      setData((prev) => (prev ? prev.filter((item) => item._id !== id) : []));
    });

  return {
    data,
    loading,
    error,
    markRead,
    clearAll,
    removeNotification,
    setData,
  };
}
